import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [scrollIndicator] - The desktop gallery scroll indicator.
 * @property {HTMLElement} [scrollIndicatorThumb] - The moving segment of the scroll indicator.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupScrollIndicator();
    this.#setupSlideshowIndicator();
  }

  #controller = new AbortController();

  /** @type {IntersectionObserver | null} */
  #scrollIndicatorObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#scrollIndicatorObserver?.disconnect();
    this.#scrollIndicatorObserver = null;
  }

  /**
   * Tracks which gallery image the viewport is on and drives the scroll indicator.
   *
   * Uses an IntersectionObserver whose root is collapsed to a zero-height band across the middle of
   * the viewport, so exactly one image can intersect at a time and no scroll handler -- and no
   * layout read -- is needed on a long, image-heavy page.
   */
  #setupScrollIndicator() {
    const indicator = this.refs.scrollIndicator;
    const media = this.media;

    if (!indicator || !media || media.length < 2) return;

    this.#scrollIndicatorObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const index = media.indexOf(/** @type {HTMLElement} */ (entry.target));

          if (index >= 0) indicator.style.setProperty('--gallery-indicator-index', `${index}`);
        }
      },
      { rootMargin: '-50% 0px -50% 0px' }
    );

    for (const element of media) {
      this.#scrollIndicatorObserver.observe(element);
    }
  }

  /**
   * Drives the mobile scroll indicator from the slideshow's own notion of the current slide.
   *
   * The mobile gallery is a swipeable slideshow rather than a scroll column, so there is nothing
   * for the observer above to watch. The slideshow already resolves swipes, drags and scrolls to a
   * slide index and announces it, so this only has to listen -- no second source of truth, and no
   * scroll or touchmove handler of its own.
   *
   * The indicator is looked up rather than taken from `refs`: it is painted over the slideshow and
   * so is rendered inside `<slideshow-component>`, and `Component` assigns every `ref` to the
   * closest component ancestor -- which makes it the slideshow's ref, never the gallery's. The
   * event still reaches us, because it bubbles.
   */
  #setupSlideshowIndicator() {
    const indicator = this.querySelector('.media-gallery__scroll-indicator--horizontal');

    if (!(indicator instanceof HTMLElement)) return;

    this.#setSlideshowIndex(indicator, this.refs.slideshow?.current ?? 0);

    this.addEventListener(
      SlideshowSelectEvent.eventName,
      (event) => {
        this.#setSlideshowIndex(indicator, /** @type {SlideshowSelectEvent} */ (event).detail.index);
      },
      { signal: this.#controller.signal }
    );
  }

  /**
   * @param {HTMLElement} indicator - The indicator element.
   * @param {number} index - The zero-based index of the current slide.
   */
  #setSlideshowIndex(indicator, index) {
    if (!Number.isFinite(index) || index < 0) return;

    indicator.style.setProperty('--gallery-indicator-index', `${index}`);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
